import React, { useEffect, useState } from 'react';
import useTimeTrackingStore, {
  getTimerSnapshot, useTimerUserid, useTimerPaused, useTimerAutoPaused, useTimerDefaultPaused,
} from '../../../state/application/useTimeTrackingStore';
import { useCurrentMonthCptSeconds } from '../../../state/data/interaction';
import { formatTimer } from '../../../logic/timeTracking';

// Always-on interaction timer chip. Shown value = persisted current-month
// total (server seed) + live NOT-yet-flushed seconds — never seed + full
// elapsed, which would double-count flushed chunks once the seed refetches.
// Clicking the value opens the Time log modal (onOpen).
export default function TimerChip({ userid, onOpen }) {
  const uid = userid != null ? String(userid) : null;
  const storeUid = useTimerUserid();
  const paused = useTimerPaused();
  const autoPaused = useTimerAutoPaused();
  const defaultPaused = useTimerDefaultPaused();
  const pauseTimer = useTimeTrackingStore((s) => s.pause);
  const resumeTimer = useTimeTrackingStore((s) => s.resume);
  const active = uid != null && storeUid === uid;
  const seed = useCurrentMonthCptSeconds(active ? uid : null);

  // Display-only 1 s tick — elapsed is derived from the store's timestamps
  // (never a store selector: Date.now() snapshots break useSyncExternalStore).
  const [liveSeconds, setLiveSeconds] = useState(0);
  useEffect(() => {
    if (!active) return undefined;
    const tick = () => {
      const snap = getTimerSnapshot();
      setLiveSeconds(snap.userid === uid ? snap.unflushedSeconds : 0);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [active, uid]);

  if (!active) return null;

  const total = (seed.data ?? 0) + liveSeconds;
  const stateLabel = autoPaused ? 'Auto-paused' : paused ? 'Paused' : null;
  const title = autoPaused
    ? 'Timer auto-paused after 4 hours — resume to keep counting'
    : defaultPaused
      ? 'Timer starts paused for this account — press play to count this session'
      : paused
        ? 'Timer paused'
        : 'Time tracked with this patient this month (running)';

  return (
    <div className={`timer-chip${paused ? ' is-paused' : ''}`} title={title}>
      <button
        type="button"
        className="timer-chip__main"
        aria-label="Open time log"
        onClick={onOpen}
        disabled={!onOpen}
      >
        <span className="timer-chip__dot" aria-hidden="true" />
        <span className="timer-chip__value">{formatTimer(total)}</span>
        {stateLabel && <span className="timer-chip__state">{stateLabel}</span>}
      </button>
      <button
        type="button"
        className="timer-chip__btn"
        aria-label={paused ? 'Resume timer' : 'Pause timer'}
        onClick={() => (paused ? resumeTimer() : pauseTimer())}
      >
        {paused ? (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><polygon points="6 4 20 12 6 20" /></svg>
        ) : (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><rect x="5" y="4" width="5" height="16" rx="1" /><rect x="14" y="4" width="5" height="16" rx="1" /></svg>
        )}
      </button>
    </div>
  );
}
